import React, { useState, useEffect } from 'react';
import {
  X, Download, Printer, Calendar, RefreshCw, FileText, Filter, Clock
} from 'lucide-react';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const VIOLET = '#667eea', VIOLET_DK = '#764ba2';

const TrialBalance = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    AsOnDate: new Date().toISOString().slice(0, 10),
    ShowZeroBalance: false,
    AccountGroup: ''
  });

  const [reportData, setReportData] = useState(null);
  const [accountGroups, setAccountGroups] = useState([]);

  const session = getSessionData();
  function getSessionData() {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch (error) { console.error('Error parsing session:', error); return null; }
  }

  useEffect(() => { loadAccountGroups(); }, []);

  const loadAccountGroups = async () => {
    try {
      const response = await apiClient.get('/AccountGroups.php');
      if (response.data.success) setAccountGroups(response.data.data || []);
    } catch (err) { console.error('Error loading account groups:', err); }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleGenerateReport = async () => {
    if (!filters.AsOnDate) { setError('Please select a date'); return; }
    setLoading(true); setError('');
    try {
      const response = await apiClient.get('/TrialBalance.php', { params: filters });
      if (response.data.success) setReportData(response.data.data);
      else setError(response.data.message || 'Failed to generate report');
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report: ' + (err.response?.data?.message || err.message));
    } finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    if (!reportData || !reportData.accounts || reportData.accounts.length === 0) { alert('No data to export'); return; }
    const headers = ['Account Code', 'Account Name', 'Group', 'Opening Balance', 'Debit', 'Credit', 'Closing Balance', 'Dr/Cr'];
    const rows = reportData.accounts.map(acc => [
      acc.ACHeadCode || '', acc.ACHeadName || '', acc.GroupName || '',
      acc.OpeningBalance || '0.00', acc.DebitTotal || '0.00', acc.CreditTotal || '0.00',
      acc.ClosingBalance || '0.00', acc.BalanceType || 'Dr'
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `trial_balance_${filters.AsOnDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleClearFilters = () => {
    setFilters({ AsOnDate: new Date().toISOString().slice(0, 10), ShowZeroBalance: false, AccountGroup: '' });
    setReportData(null);
  };

  // Shared classes
  const btnBase = 'flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-200';
  const btnOutline = `${btnBase} bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50 hover:border-violet-400 hover:text-violet-500 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0`;
  const inpCls = 'px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-violet-400 focus:shadow-[0_0_0_4px_rgba(102,126,234,0.1)] focus:-translate-y-px hover:border-gray-300';

  if (!session?.companyid) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto bg-gray-50 min-h-[calc(100vh-100px)]">
        <div className="flex justify-between items-center mb-6 bg-white px-6 py-5 rounded-xl shadow-sm">
          <div>
            <h2 className="text-[28px] font-bold m-0 mb-1 bg-clip-text text-transparent" style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>Session Required</h2>
            <p className="text-gray-500 text-sm m-0 font-medium">Please select company and year</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center py-20 text-gray-500">
          <div className="text-[64px] opacity-30 mb-5">🔒</div>
          <h3 className="text-[22px] font-bold text-gray-700 m-0 mb-2.5">Session Required</h3>
          <p className="text-[15px] text-gray-500 m-0">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto bg-gray-50 min-h-[calc(100vh-100px)]">
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          .print-only { display: grid !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-center mb-6 bg-white px-6 py-5 rounded-xl shadow-sm">
        <div>
          <h2 className="text-[28px] font-bold m-0 mb-1 bg-clip-text text-transparent" style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>Trial Balance</h2>
          <p className="text-gray-500 text-sm m-0 font-medium">Complete Account Summary Report</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="no-print flex gap-3 items-center mb-6 flex-wrap bg-white p-4 rounded-xl shadow-sm">
        <button onClick={handleGenerateReport} disabled={loading}
          className={`${btnBase} text-white shadow-[0_4px_12px_rgba(102,126,234,0.3)] hover:-translate-y-[3px] hover:shadow-[0_8px_20px_rgba(102,126,234,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none`}
          style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
          <RefreshCw size={18}/> Generate Report
        </button>
        <button onClick={handlePrint} disabled={!reportData || loading} className={btnOutline}><Printer size={18}/> Print</button>
        <button onClick={handleExportCSV} disabled={!reportData || loading} className={btnOutline}><Download size={18}/> Export CSV</button>
        <button onClick={handleClearFilters} disabled={loading} className={btnOutline}><RefreshCw size={18}/> Clear</button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="px-5 py-4 rounded-lg mb-6 flex items-center gap-3 text-sm font-semibold border-2"
          style={{ background:'linear-gradient(135deg,#fee2e2,#fecaca)', color:'#991b1b', borderColor:'#ef4444' }}>
          <div className="text-2xl flex-shrink-0">⚠</div>
          <div><strong>Error:</strong> {error}</div>
        </div>
      )}

      {/* Filters */}
      <div className="no-print bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-800 m-0 mb-5 pb-3 border-b-2 border-gray-200 flex items-center gap-2.5 uppercase tracking-wide">
          <span className="w-1 h-6 rounded-sm inline-block" style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}/>
          <Filter size={18}/> Report Filters
        </h3>
        <div className="grid gap-5" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">As On Date <span className="text-red-600 text-base">*</span></label>
            <input type="date" name="AsOnDate" value={filters.AsOnDate} onChange={handleFilterChange} required className={inpCls}/>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Account Group</label>
            <select name="AccountGroup" value={filters.AccountGroup} onChange={handleFilterChange} className={inpCls}>
              <option value="">All Groups</option>
              {accountGroups.map(group => <option key={group.acgroupid} value={group.acgroupid}>{group.acgroupname} ({group.actype})</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
              <input type="checkbox" name="ShowZeroBalance" id="ShowZeroBalance" checked={filters.ShowZeroBalance} onChange={handleFilterChange}
                className="w-5 h-5 cursor-pointer accent-violet-500"/>
              <label htmlFor="ShowZeroBalance" className="cursor-pointer text-sm font-semibold text-gray-700 m-0">Show Zero Balance Accounts</label>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-20 bg-white rounded-xl shadow-sm">
          <div className="w-[60px] h-[60px] border-[6px] border-gray-100 rounded-full mx-auto mb-6" style={{ borderTopColor:VIOLET, animation:'spin 1s linear infinite' }}/>
          <p className="text-lg font-bold text-gray-500 uppercase tracking-wide">Generating trial balance...</p>
          <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Report Display */}
      {reportData && !loading && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b-[3px] border-gray-200">
            <h1 className="text-[32px] font-extrabold m-0 mb-2 bg-clip-text text-transparent" style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
              {reportData.company?.CompanyName || 'Company Name'}
            </h1>
            <h2 className="text-2xl font-semibold text-gray-600 m-0 mb-4 uppercase tracking-wide">Trial Balance</h2>
            <div className="flex justify-center gap-6 flex-wrap text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5"><Calendar size={16}/><span>As On: {new Date(filters.AsOnDate).toLocaleDateString()}</span></div>
              <div className="flex items-center gap-1.5"><FileText size={16}/><span>Financial Year: {reportData.year?.YearName || 'N/A'}</span></div>
              <div className="flex items-center gap-1.5"><Clock size={16}/><span>Generated: {new Date().toLocaleString()}</span></div>
            </div>
          </div>

          {/* Table */}
          {reportData.accounts && reportData.accounts.length > 0 ? (
            <div className="overflow-x-auto mb-6 rounded-xl border-2 border-gray-200">
              <table className="w-full border-collapse text-sm">
                <thead className="text-white sticky top-0 z-10" style={{ backgroundImage:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
                  <tr>
                    <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:80 }}>Code</th>
                    <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3' }}>Account Name</th>
                    <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3' }}>Group</th>
                    <th className="px-4 py-3.5 text-right text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:120 }}>Opening Bal</th>
                    <th className="px-4 py-3.5 text-right text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:120 }}>Debit</th>
                    <th className="px-4 py-3.5 text-right text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:120 }}>Credit</th>
                    <th className="px-4 py-3.5 text-right text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:120 }}>Closing Bal</th>
                    <th className="px-4 py-3.5 text-center text-xs font-bold uppercase tracking-wider border-b-[3px]" style={{ borderColor:'#5568d3', width:60 }}>Dr/Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.accounts.map((account, index) => (
                    <tr key={index} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-all duration-150">
                      <td className="px-4 py-3.5 text-gray-700 align-middle">
                        <span className="font-mono text-[13px] font-bold px-2.5 py-1 rounded-md inline-block" style={{ color:VIOLET, background:'#ede9fe' }}>{account.ACHeadCode}</span>
                      </td>
                      <td className="px-4 py-3.5 text-gray-700 align-middle"><span className="font-semibold text-gray-800 text-[15px]">{account.ACHeadName}</span></td>
                      <td className="px-4 py-3.5 align-middle text-[13px] text-gray-500">{account.GroupName}</td>
                      <td className="px-4 py-3.5 text-right font-mono font-semibold align-middle">{parseFloat(account.OpeningBalance || 0).toFixed(2)}</td>
                      <td className="px-4 py-3.5 text-right font-mono font-bold align-middle text-emerald-600">{parseFloat(account.DebitTotal || 0).toFixed(2)}</td>
                      <td className="px-4 py-3.5 text-right font-mono font-bold align-middle text-red-600">{parseFloat(account.CreditTotal || 0).toFixed(2)}</td>
                      <td className="px-4 py-3.5 text-right font-mono font-bold align-middle">{parseFloat(account.ClosingBalance || 0).toFixed(2)}</td>
                      <td className="px-4 py-3.5 text-center align-middle">
                        <span className="inline-flex items-center justify-center px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide min-w-[50px] border-2"
                          style={account.BalanceType === 'Dr'
                            ? { backgroundImage:'linear-gradient(135deg,#dcfce7,#bbf7d0)', color:'#166534', borderColor:'#10b981' }
                            : { backgroundImage:'linear-gradient(135deg,#fee2e2,#fecaca)', color:'#991b1b', borderColor:'#ef4444' }}>
                          {account.BalanceType || 'Dr'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="font-bold text-[15px]" style={{ backgroundImage:'linear-gradient(135deg,#f9fafb,#f3f4f6)', borderTop:`3px solid ${VIOLET}` }}>
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-right uppercase tracking-wide font-extrabold text-gray-800">Totals:</td>
                    <td className="px-4 py-4 text-right font-mono text-lg font-extrabold text-emerald-600">{parseFloat(reportData.totals?.TotalDebit || 0).toFixed(2)}</td>
                    <td className="px-4 py-4 text-right font-mono text-lg font-extrabold text-red-600">{parseFloat(reportData.totals?.TotalCredit || 0).toFixed(2)}</td>
                    <td colSpan={2}/>
                  </tr>
                  {reportData.totals?.Difference && parseFloat(reportData.totals.Difference) !== 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 text-right uppercase tracking-wide font-extrabold" style={{ color:'#991b1b' }}>Difference:</td>
                      <td className="px-4 py-4 text-right font-mono text-lg font-extrabold" style={{ color:'#991b1b' }}>{parseFloat(reportData.totals.Difference).toFixed(2)}</td>
                      <td/>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500">
              <div className="text-[64px] opacity-30 mb-5">📊</div>
              <h3 className="text-[22px] font-bold text-gray-700 m-0 mb-2.5">No Accounts Found</h3>
              <p className="text-[15px] text-gray-500 m-0">No accounts found for the selected filters</p>
            </div>
          )}

          {/* Balance Check */}
          {reportData.totals && (
            <div className="px-6 py-5 rounded-xl mt-6 flex items-center gap-4 border-[3px]"
              style={parseFloat(reportData.totals.Difference || 0) === 0
                ? { backgroundImage:'linear-gradient(135deg,#dcfce7,#bbf7d0)', borderColor:'#10b981', color:'#166534' }
                : { backgroundImage:'linear-gradient(135deg,#fee2e2,#fecaca)', borderColor:'#ef4444', color:'#991b1b' }}>
              <div className="text-5xl leading-none">{parseFloat(reportData.totals.Difference || 0) === 0 ? '✓' : '⚠'}</div>
              <div className="flex-1">
                {parseFloat(reportData.totals.Difference || 0) === 0 ? (
                  <>
                    <h3 className="text-lg font-extrabold m-0 mb-1.5 uppercase tracking-wide">Trial Balance is Balanced!</h3>
                    <p className="text-sm font-semibold m-0">Total Debit = Total Credit = {parseFloat(reportData.totals.TotalDebit || 0).toFixed(2)}</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-extrabold m-0 mb-1.5 uppercase tracking-wide">Trial Balance Not Balanced!</h3>
                    <p className="text-sm font-semibold m-0">Difference: {parseFloat(reportData.totals.Difference || 0).toFixed(2)}</p>
                    <p className="text-[13px] font-semibold m-0 mt-1">Please check your journal entries for errors.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Signature (print only) */}
          <div className="print-only grid-cols-3 gap-16 mt-20 pt-10 border-t-2 border-gray-200">
            {['Prepared By','Checked By','Approved By'].map(label=>(
              <div key={label} className="text-center">
                <div className="border-t-2 border-gray-800 pt-2.5 mt-16 text-sm font-bold text-gray-700 uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialBalance;